import type { VsmContext, PageConfigEntry, PageConfigFn } from '@wix/thunderbolt-vsm-api-types'
import type { HoverBoxPageConfig, CompData } from '../types'
import type { AnimationsBehavior, ComponentOverride, Modes } from '@wix/thunderbolt-becky-types'
import { HOVER_SUFFIX, MODES_TYPE } from '@wix/thunderbolt-commons'
import { ComponentType, name } from '../symbols'

const defaultTransitionType = 'Scale'
const ComponentToTransitionType: Record<string, string> = {
	SiteButton: 'NoScale',
	WRichText: 'NoDimensions',
}

function getCompOverridesByModeId(compModes: Modes, modeId: string): ComponentOverride | undefined {
	const allOverrides = compModes.overrides ?? []
	return allOverrides.find((override) => override.modeIds?.[0] === modeId)
}

function isCompHiddenByMode(compModes: Modes, modeId: string): boolean {
	const modeOverride = getCompOverridesByModeId(compModes, modeId)
	return modeOverride?.isHiddenByModes !== undefined ? !!modeOverride.isHiddenByModes : !!compModes.isHiddenByModes
}

function fixBehaviorAnimationName(
	modeChangeAnimation: AnimationsBehavior,
	compType: string | undefined
): AnimationsBehavior {
	const transitionType = (compType && ComponentToTransitionType[compType]) ?? defaultTransitionType
	return { ...modeChangeAnimation, name: (modeChangeAnimation.name ?? '') + transitionType }
}

function getComponentBehaviors(api: VsmContext['api'], compId: string): Record<string, unknown> {
	const compType = api.components.getCompType(compId)
	const animations = api.components.getAnimations(compId)
	const byAction: Record<string, AnimationsBehavior> = {}
	for (const behavior of animations) {
		if (behavior.action) byAction[behavior.action] = behavior
	}
	const result: Record<string, unknown> = {}
	if (byAction.modeIn) result.in = byAction.modeIn
	if (byAction.modeOut) result.out = byAction.modeOut
	if (byAction.modeChange) result.change = fixBehaviorAnimationName(byAction.modeChange, compType)
	return result
}

function getModeIds(comp: { modes?: Modes }): { defaultModeId: string; hoverModeId: string } {
	const definitions = comp.modes?.definitions ?? []
	let defaultModeId = ''
	let hoverModeId = ''
	for (const def of definitions) {
		if (def.type === MODES_TYPE.default && def.modeId) defaultModeId = def.modeId
		if (def.type === MODES_TYPE.hover && def.modeId) hoverModeId = def.modeId
	}
	return { defaultModeId, hoverModeId }
}

function getVisibleChildrenForMode(api: VsmContext['api'], children: string[], modeId: string): Set<string> {
	const visible = new Set<string>()
	for (const childId of children) {
		const childModes = api.components.getCompModes(childId) ?? ({} as Modes)
		if (!isCompHiddenByMode(childModes, modeId)) {
			visible.add(childId)
		}
	}
	return visible
}

const hoverBoxPageConfig: PageConfigFn<HoverBoxPageConfig> = ({ api }: VsmContext) => {
	if (api.env.isMobileView()) {
		return { shouldLoadPageFeature: false }
	}

	const hoverBoxes = api.components.getComponentsByType(ComponentType)
	if (Object.keys(hoverBoxes).length === 0) {
		return { shouldLoadPageFeature: false }
	}

	const defaultModeComps: Record<string, string[]> = {}
	const hoverModeComps: Record<string, string[]> = {}
	const hoverBoxChildrenData: Record<string, Record<string, CompData>> = {}
	const fixedPositionHoverBoxes: string[] = []
	const hoverBoxParent: Record<string, string> = {}

	for (const [compId, comp] of Object.entries(hoverBoxes)) {
		const { defaultModeId, hoverModeId } = getModeIds(comp)
		const children = comp.components ?? []

		const defaultVisible = getVisibleChildrenForMode(api, children, defaultModeId)
		const hoverVisible = getVisibleChildrenForMode(api, children, hoverModeId)

		defaultModeComps[compId] = Array.from(defaultVisible)

		const hoverCompId = compId + HOVER_SUFFIX
		const hoverChildren: string[] = []
		for (const childId of hoverVisible) {
			hoverChildren.push(defaultVisible.has(childId) ? childId + HOVER_SUFFIX : childId)
		}
		hoverModeComps[hoverCompId] = hoverChildren

		const defaultChildrenData: Record<string, CompData> = {}
		for (const childId of defaultVisible) {
			defaultChildrenData[childId] = {
				behavior: getComponentBehaviors(api, childId) as CompData['behavior'],
				layout: api.components.getCompLayout(childId)!,
			}
		}
		hoverBoxChildrenData[compId] = defaultChildrenData

		const hoverChildrenData: Record<string, CompData> = {}
		for (const childId of hoverVisible) {
			const key = defaultVisible.has(childId) ? childId + HOVER_SUFFIX : childId
			hoverChildrenData[key] = {
				behavior: getComponentBehaviors(api, childId) as CompData['behavior'],
				layout: api.components.getCompLayout(childId)!,
			}
		}
		hoverBoxChildrenData[hoverCompId] = hoverChildrenData

		if (comp.layout?.fixedPosition) {
			fixedPositionHoverBoxes.push(compId)
			fixedPositionHoverBoxes.push(hoverCompId)
		}

		if (comp.parent) {
			hoverBoxParent[compId] = comp.parent
		}
	}

	return {
		shouldLoadPageFeature: true,
		pageConfig: {
			defaultModeComps,
			hoverModeComps,
			fixedPositionHoverBoxes,
			hoverBoxParent,
			hoverBoxChildrenData,
		},
	}
}

export const pageConfig: PageConfigEntry<HoverBoxPageConfig> = {
	name,
	configFactory: hoverBoxPageConfig,
	experiment: 'specs.thunderbolt.vsmPageConfigs',
}
